import Upload from 'hooks/useDirectUpload/Upload';
import { createPresigned } from 'services/api/uploads';

// Documents captured by the extension during a portal retrieval (OLIO-2605): the
// bytes ride the retrieval result as base64 file entries under the `attachments`
// output. Before the outputs go to the api we direct-upload each file (the same
// ActiveStorage path as a hand-uploaded packet) and replace the entry with
// {signedId, filename} — the api mints those into Attachments.

export interface PortalFileValue {
  kind: 'file';
  filename?: string;
  contentType?: string;
  size?: number;
  dataBase64: string;
}

function isPortalFileArray(value: unknown): value is PortalFileValue[] {
  return Array.isArray(value) && value.length > 0 && value.every((entry) => entry && entry.kind === 'file');
}

function toFile(entry: PortalFileValue, index: number): File {
  const binary = atob(entry.dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const type = entry.contentType || 'application/octet-stream';
  return new File([bytes], entry.filename || `document-${index + 1}`, { type });
}

async function uploadFile(entry: PortalFileValue, index: number, uploadDomain?: string) {
  const upload = new Upload({ id: String(index), file: toFile(entry, index), uploadDomain });
  await upload.start(createPresigned, () => {});
  if (upload.hasError || !upload.signedId) {
    throw new Error(`Uploading captured document "${upload.filename}" failed.`);
  }
  return { signedId: upload.signedId, filename: upload.filename };
}

// Replace every file-entry array in the retrieval's values with uploaded
// {signedId, filename} entries; everything else passes through untouched.
export async function uploadPortalDocuments(
  values: Record<string, unknown>,
  uploadDomain?: string
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = { ...values };
  for (const [name, value] of Object.entries(values)) {
    if (!isPortalFileArray(value)) continue;
    out[name] = await Promise.all(value.map((entry, index) => uploadFile(entry, index, uploadDomain)));
  }
  return out;
}
